import Component from '@glimmer/component';
import { isTesting, macroCondition } from '@embroider/macros';
import { restartableTask, timeout } from 'ember-concurrency';
import { didResize } from 'ember-resize-modifier';

interface ChartResizerSentinelSignature {
  Args: {
    didResize?: (width: number) => void;
    previousWidth?: number;
    debounceRate?: number;
  };
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

// ChartResizerSentinel wraps yielded chart content purely to be measured, so
// `chart-resizer` can react to its own rendered width without a resize hook
// of its own. Debounced (0 in tests) so an expensive chart redraw isn't
// triggered on every frame of a continuous drag-resize -- ResizeObserver
// itself does not debounce.
export default class ChartResizerSentinel extends Component<ChartResizerSentinelSignature> {
  previousWidth = this.args.previousWidth ?? 480;
  hasMeasured = false;

  get debounceRate() {
    if (macroCondition(isTesting())) {
      return 0;
    }
    return this.args.debounceRate ?? 250;
  }

  onResize = (entry: ResizeObserverEntry) => {
    void this.resizeTask.perform(Math.floor(entry.contentRect.width));
  };

  // The observer's initial callback reports the real layout width, not a
  // drag in progress, so it shouldn't wait out the debounce -- otherwise
  // anything that captures/serialises the chart shortly after mount (PDF
  // export, report snapshots) can run before the first resize lands and
  // freeze in the default width instead.
  resizeTask = restartableTask(async (width: number) => {
    if (this.hasMeasured) {
      await timeout(this.debounceRate);
    }
    this.hasMeasured = true;
    if (width !== this.previousWidth) {
      this.previousWidth = width;
      this.args.didResize?.(width);
    }
  });

  <template>
    <div {{didResize this.onResize}} ...attributes>{{yield}}</div>
  </template>
}
